import { helpers, type PluginConfiguration } from '../helpers'
import { icons } from '../icons'

type FeatureFlagData = {
  enabled: boolean
  original_enabled?: boolean
  metadata?: { description?: string }
  reason?: unknown
}

type FeatureFlagsPluginProps = {
  allFeatureFlags: readonly string[]
  /**
   * If true, will use custom local storage key for overriding feature flags.
   *
   * Useful for FollowUp that doesn't use PostHog's persistent storage.
   */
  useLocalStorage?: boolean
  posthogFeatureFlagsMethods: {
    getFlagsWithDetails: () => Record<string, FeatureFlagData>
    overrideFeatureFlags: (options: { flags: Record<string, boolean> } | false) => void
  }
}

const isFlagOverriddenLocally = (flagValue?: FeatureFlagData): boolean => {
  // if not defined at all, flag value is not set in PH -> is not overridden yet
  if (flagValue === undefined) {
    return false
  }
  // if reason and original_enabled are set, flag value is set in PH -> compare original and current value
  if (flagValue?.reason !== undefined && flagValue?.original_enabled !== undefined) {
    return flagValue?.original_enabled !== flagValue?.enabled
  }
  // if reason and original_enabled are not set, flag value is not set in PH -> must be overridden
  if (flagValue?.reason === undefined && flagValue?.original_enabled === undefined) {
    return true
  }
  return false
}

const FEATURE_FLAGS_LOCAL_STORAGE_KEY = 'fu-devtools-feature-flags'

export const createFeatureFlagsPlugin = ({
  allFeatureFlags,
  posthogFeatureFlagsMethods,
  useLocalStorage,
}: FeatureFlagsPluginProps): PluginConfiguration => {
  if (useLocalStorage) {
    try {
      const windowLocalStorageFeatureFlags = window.localStorage.getItem(
        FEATURE_FLAGS_LOCAL_STORAGE_KEY
      )
      if (windowLocalStorageFeatureFlags) {
        posthogFeatureFlagsMethods.overrideFeatureFlags({
          flags: JSON.parse(windowLocalStorageFeatureFlags),
        })
      }
    } catch (error) {
      console.error('[FU devtools]: Error parsing feature flags from local storage: ', error)
    }
  }

  const buildOverrideIndicator = (flagValue: FeatureFlagData | undefined): Element | null => {
    if (!isFlagOverriddenLocally(flagValue)) {
      return null
    }
    const isDimmed = flagValue?.enabled === false && flagValue?.original_enabled === undefined
    return helpers.createHTMLElement({
      element: 'div',
      className: `text-sm ${isDimmed ? 'text-[#334155]' : 'text-[#FA914B]'}`,
      content: () =>
        helpers.createHTMLElements([
          { element: 'span', content: 'Locally overridden to: ' },
          { element: 'b', content: `${flagValue?.enabled}` },
          { element: 'span', content: ' (Posthog: ' },
          { element: 'b', content: `${flagValue?.original_enabled}` },
          { element: 'span', content: ')' },
        ]),
    })
  }

  const refreshFlagStates = () => {
    const freshFlagsValues = posthogFeatureFlagsMethods.getFlagsWithDetails() ?? {}
    for (const flag of allFeatureFlags) {
      const checkbox = document.querySelector<HTMLInputElement>(
        `[data-fu-tools-feature-flag-toggle="${flag}"]`
      )
      if (checkbox) {
        checkbox.checked = freshFlagsValues[flag]?.enabled ?? false
      }

      const indicatorContainer = document.querySelector(
        `[data-fu-tools-feature-flag-override="${flag}"]`
      )
      if (!indicatorContainer) {
        continue
      }
      indicatorContainer.innerHTML = ''
      const indicator = buildOverrideIndicator(freshFlagsValues[flag])
      if (indicator) {
        indicatorContainer.appendChild(indicator)
      }
    }
  }

  const config: PluginConfiguration = {
    name: 'Feature Flags',
    id: 'feature-flags',
    icon: icons.featureFlags,
    extra: '',
    layout: {
      type: 'full',
      render: () => {
        const flagsValues = posthogFeatureFlagsMethods.getFlagsWithDetails() ?? {}
        const createdElements = helpers.createHTMLElements([
          {
            className:
              'pt-4 px-4 flex items-center justify-between mb-2 text-white gap-2 font-semibold',
            content: 'Feature Flags',
          },
          {
            className: 'flex items-center gap-2 mx-2',
            content: () =>
              helpers.createHTMLElements([
                {
                  className:
                    'flex flex-1 justify-center border-0 text-white! transition-all bg-[#334155] hover:bg-[#1e293b]! rounded-md cursor-pointer p-2',
                  content: '🧹 Clear all flag overrides',
                  title: 'Will clear all local flag overrides',
                  id: 'fu-tools-feature-flags-clear-all-overrides',
                },
              ]),
          },
          {
            className: 'flex flex-col gap-4 p-4',
            content: () =>
              helpers.createHTMLElements(
                allFeatureFlags.map(flag => {
                  const flagValue = flagsValues[flag]
                  const isFlagOverridden = isFlagOverriddenLocally(flagValue)

                  return {
                    element: 'div',
                    className: 'flex-col gap-2 items-center',
                    id: `fu-tools-feature-flag-toggle_${flag}`,
                    title: flagValue?.metadata?.description ?? 'No description available.',
                    content: () =>
                      helpers.createHTMLElements([
                        {
                          element: 'div',
                          className: 'flex gap-2 items-center',
                          content: () =>
                            helpers.createHTMLElements([
                              {
                                element: 'label',
                                className:
                                  'inline-flex gap-2 items-center cursor-pointer text-white',
                                content: () =>
                                  helpers.createHTMLElements([
                                    {
                                      element: 'span',
                                      content: `${flag}:`,
                                    },
                                    {
                                      element: 'input',
                                      attributes: {
                                        type: 'checkbox',
                                        ...(flagValue?.enabled ? { checked: '' } : {}),
                                        'data-fu-tools-feature-flag-toggle': flag,
                                      },
                                      className: 'sr-only peer',
                                    },
                                    {
                                      element: 'div',
                                      className:
                                        "relative w-7 h-4 bg-[#334155] rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-[#cbd5e1] after:border after:rounded-full after:w-3 after:h-3 after:transition-all peer-checked:bg-[#22d3ee]",
                                    },
                                  ]),
                              },
                              {
                                element: 'div',
                                attributes: {
                                  'data-fu-tools-feature-flag-override': flag,
                                },
                                content: isFlagOverridden
                                  ? () => {
                                      const fragment = document.createDocumentFragment()
                                      const indicator = buildOverrideIndicator(flagValue)
                                      if (indicator) {
                                        fragment.appendChild(indicator)
                                      }
                                      return fragment
                                    }
                                  : '',
                              },
                            ]),
                        },
                        ...(flagValue?.metadata?.description
                          ? [
                              {
                                element: 'div',
                                className: 'text-sm text-[#6F91B7]',
                                content: () =>
                                  helpers.createHTMLElements([
                                    {
                                      element: 'span',
                                      content: 'Description: ',
                                    },
                                    {
                                      element: 'span',
                                      content: flagValue.metadata?.description ?? '',
                                    },
                                  ]),
                              },
                            ]
                          : []),
                      ]),
                  }
                })
              ),
          },
        ])

        const tempDiv = document.createElement('div')
        tempDiv.className = 'relative'
        tempDiv.appendChild(createdElements)
        return tempDiv.outerHTML
      },
    },
    init: () => {
      const featureFlagToggles = document.querySelectorAll<HTMLInputElement>(
        '[data-fu-tools-feature-flag-toggle]'
      )

      const applyCurrentToggles = () => {
        const overrides = Array.from(featureFlagToggles).reduce<Record<string, boolean>>(
          (acc, featureFlagToggle) => {
            const flag = featureFlagToggle.getAttribute('data-fu-tools-feature-flag-toggle')
            if (!flag) {
              return acc
            }
            return { ...acc, [flag]: featureFlagToggle.checked }
          },
          {}
        )
        if (Object.keys(overrides).length === 0) {
          return
        }
        posthogFeatureFlagsMethods.overrideFeatureFlags({ flags: overrides })
        if (useLocalStorage) {
          try {
            window.localStorage.setItem(FEATURE_FLAGS_LOCAL_STORAGE_KEY, JSON.stringify(overrides))
          } catch (error) {
            console.error('[FU devtools]: Error saving feature flags to local storage: ', error)
          }
        }
        refreshFlagStates()
      }

      for (const toggle of featureFlagToggles) {
        toggle.addEventListener('change', applyCurrentToggles)
      }

      const clearAllFlagsOverridesButton = document.querySelector(
        '#fu-tools-feature-flags-clear-all-overrides'
      )
      clearAllFlagsOverridesButton?.addEventListener('click', () => {
        posthogFeatureFlagsMethods.overrideFeatureFlags(false)
        if (useLocalStorage) {
          try {
            window.localStorage.removeItem(FEATURE_FLAGS_LOCAL_STORAGE_KEY)
          } catch (error) {
            console.error('[FU devtools]: Error removing feature flags from local storage: ', error)
          }
        }
        refreshFlagStates()
      })
    },
    render: () => config.layout.render({}),
  }
  return config
}
